/**
 * @typedef {Object} AddonGroupNoticeCTA
 * @property {String} label - The label of the notice button.
 * @property {String} [icon] - An optional icon that appears before the label.
 * @property {String} url - The URL the button links to.
 */

/**
 * @typedef {Object} AddonGroupNotice
 * @property {String} message - The message displayed in the notice.
 * @property {"error"|"warning"|"info"|"locked"} type - The type of notice displayed.
 * @property {AddonGroupNoticeCTA} [cta] - Optional. A call-to-action button on the notice.
 */

/**
 * @typedef {Object} AddonDeveloper
 * @property {String} name - The name of the developer or company.
 * @property {String} url - The URL of the developer's website.
 */

/**
 * @typedef {Object} AddonDetail
 * @property {String} label - The label of the detail (e.g., "Version").
 * @property {String} value - The value of the detail (supports markdown).
 */

/**
 * @typedef {Object} AddonCTA
 * @property {String} label - The label of the call-to-action button.
 * @property {"secondary"|"primary"} type - The button type.
 * @property {String} [url] - The URL the button links to.
 * @property {String} [async_action] - The async action to perform.
 * @property {String} [icon] - The icon name used by the button.
 * @property {Boolean} [disabled] - Whether the button is disabled.
 */

/**
 * @typedef {Object} Addon
 * @property {String} scenario_id - The scenario ID, like `no-access`, `has-access-addon-not-active`, `has-access-addon-active-has-update`, `has-access-addon-active`, etc.
 * @property {String} id - A unique identifier for the addon.
 * @property {String} icon - The icon name used by the addon.
 * @property {String} name - A human-readable name for the addon.
 * @property {String} description - A short description of the addon.
 * @property {Boolean} is_installed - Whether the addon is installed on the site.
 * @property {Boolean} is_official - Whether the addon is an official addon.
 * @property {AddonDeveloper} developer - Information about the addon’s developer.
 * @property {AddonDetail[]} details - An array of extra details for the addon.
 * @property {AddonCTA[]} [cta] - An optional array of call-to-action buttons for the addon.
 */

/**
 * @typedef {Object} AddonsGroup
 * @property {String} scenario_id - The scenario ID, like `pro-not-installed-requires-pro-basic`, `pro-not-installed-requires-pro-plus`, etc.
 * @property {Boolean} has_access - Indicates whether the user has access to this group of addons.
 * @property {AddonGroupNotice} [page_notice] - Optional. A page-level notice to display if the user lacks access.
 * @property {Addon[]} addons - A list of addons to display.
 */

/**
 * @typedef {Object} LicenseOwner
 * @property {String} name - The full name of the license owner.
 * @property {String} email - The email address of the license owner.
 */

/**
 * @typedef {Object} Addons
 * @property {Number} available - The number of addons available to download for the license owner.
 * @property {Number} total - The total number of addons available under the highest plan.
 */

/**
 * @typedef {Object} CTAButton
 * @property {String} label - The label of the button.
 * @property {String} url - The URL linked by the button.
 * @property {String} [icon] - (Optional) An icon to display alongside the button label.
 */

/**
 * @typedef {Object} LicenseInformation
 * @property {LicenseOwner} license_owner - Information about the license owner.
 * @property {("lite"|"basic"|"plus"|"elite")} current_plan - The current plan of the license.
 * @property {Addons} addons - Information about available addons.
 * @property {("active"|"inactive"|"expired")} status - The status of the license.
 * @property {CTAButton[]} cta - A list of call-to-action buttons to manage the license.
 */

/**
 * @typedef {Object} LicenseStatusMessage
 * @property {('success'|'error')} type - The type of message, e.g., "success" or "error".
 * @property {String} heading - The heading for the license message.
 * @property {String} [content] - (Optional) Additional details about the license status.
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import apiFetch from '@wordpress/api-fetch';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import components
import './item';
import './placeholder';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware(
	UncannyAutomatorBackend.rest.nonce
) );

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Web component representing a group of addons
 *
 * @augments LitElement
 */
@customElement( 'uap-page-addons-group' ) // eslint-disable-line require-jsdoc
export class AutomatorPageAddonsGroup extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The code of the list of addons to display.
	 *
	 * @type {String}
	 */
	@property( { type: String, attribute: 'list-code' } )
	accessor listCode;

	/**
	 * Addons group data.
	 *
	 * @type {AddonsGroup}
	 */
	@state( { type: Object } )
	accessor data = {};

	/**
	 * A global error message. Only used if we can't load the data for some reason
	 *
	 * @type {String}
	 */
	@state( { type: String } )
	accessor fatalErrorMessage = '';

	/**
	 * Renders the component
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	render() {
		// If there is no list ID, don't render anything
		if ( isEmpty( this.listCode ) ) {
			return;
		}

		// If there is data available, render it and stop here
		if ( ! isEmpty( this.data ) ) {
			return this._templateMain;
		}

		// Check if there is an error
		if ( ! isEmpty( this.fatalErrorMessage ) ) {
			return this._templateFatalError;
		}

		// Otherwise, get the main object. In the meantime, render the placeholder
		// We're using this approach instead of using the native `.until` directive to prevent Lit from rendering the whole UI when the Promise is resolved, and then AGAIN when one of the reactive properties change.
		return this._templatePlaceholder();
	}

	/**
	 * Returns the Promise that retrieves the data
	 *
	 * @return {Promise} The Promise that retrieves the data
	 */
	async getAddonsListData() {
		return apiFetch( {
			url: `${ UncannyAutomatorBackend.rest.base }automator/v1/addons/get-list/${ this.listCode }`
		} )
			.then( response => {
				if ( ! response.success && isDefined( response.error ) ) {
					// Show a global error
					this.fatalErrorMessage = ! isEmpty( response.error.message ) ?
						response.error.message :
						strings.unknownError;

					return;
				}

				// Cache the data
				this.data = response.data;
			} );
	}

	/**
	 * Retrieves and returns the data needed to render the license information
	 *
	 * @return {TemplateResult} The placeholder template
	 */
	_templatePlaceholder() {
		// Start call
		this.getAddonsListData().catch( error => {
			// Show a global error
			this.fatalErrorMessage = ! isEmpty( error.message )
				? error.message
				: escapeHTML( __( 'Something went wrong', 'uncanny-automator' ) );
		} );

		// In the meantime, show the placeholder
		return html`<uap-page-addons-group-placeholder></uap-page-addons-group-placeholder>`;
	}

	/**
	 * Returns the template that displays the license information
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateMain() {
		return html`
			<div class="addons-group">
				${ this._templatePageNotice }
				${ this._templateAddonsGrid }
			</div>
		`;
	}

	/**
	 * Returns the template that displays the page notice
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templatePageNotice() {
		if ( isEmpty( this.data.page_notice ) ) {
			return;
		}

		return html`
			<div 
				class="addons-group-notice"
				notice-type=${ this.data.page_notice.type }
			>
				<uo-icon 
					id=${ {
						error: 'circle-exclamation',
						warning: 'circle-exclamation',
						info: 'circle-info',
					}[ this.data.page_notice.type ] }
					icon-style="solid"
				></uo-icon>

				${ this.data.page_notice.message }

				${ this._templatePageNoticeCTA }
			</div>
		`;
	}

	/**
	 * Returns the template that displays the call-to-action button
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templatePageNoticeCTA() {
		if ( ! isDefined( this.data.page_notice.cta ) ) {
			return;
		}

		return html`
			<uo-button
				size="extra-small"
				color="secondary"
				href=${ this.data.page_notice.cta.url }
				target="_blank"
			>
				${ this.data.page_notice.cta.icon ? html`<uo-icon id=${ this.data.page_notice.cta.icon }></uo-icon>` : '' }

				${ this.data.page_notice.cta.label }
			</uo-button>
		`;
	}

	/**
	 * Returns the template that displays the grid of addons
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateAddonsGrid() {
		return html`
			<div class="addons-group-grid">
				${ repeat(
					this.data.addons || [],
					addon => addon.id,
					addon => html`
						<uap-page-addons-group-item 
							.addon=${ addon }
							?has-access=${ this.data.has_access }
						></uap-page-addons-group-item>
					`
				) }
			</div>
		`;
	}

	/**
	 * Returns the template that displays an error when there is no data at all
	 *
	 * @return {TemplateResult} The error message
	 */
	get _templateFatalError() {
		return html`
			<uo-alert
				type="error"
				heading=${ escapeHTML( __( 'Something went wrong', 'uncanny-automator' ) ) }
			>
				<p>${ this.fatalErrorMessage }</p>

				<uo-button
					size="small"
					color="secondary"

					@click=${ () => {
						// Remove message
						// This will re render the component, and given that there is no data, it'll do the call again
						this.fatalErrorMessage = '';
						this.data = null;
					} }
				>
					${ escapeHTML( __( 'Try again', 'uncanny-automator' ) ) }
				</uo-button>
			</uo-alert>
		`;
	}
}